import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { HighlightedField } from './HighlightedField.js';
export function CaptureSheet(props) {
    // Mounted anew on every opening, so the opener is captured and the field focused each time.
    return props.open ? _jsx(CaptureBody, { ...props }) : null;
}
function CaptureBody({ text, onTextChange, chips, onDismissChip, destinations, destinationId, onDestinationChange, keepOpen, onKeepOpenChange, onSave, onClose, error, labels, }) {
    const fieldRef = useRef(null);
    const panelRef = useRef(null);
    const openerRef = useRef(null);
    const titleId = useId();
    const fieldId = useId();
    const errorId = useId();
    const destinationFieldId = useId();
    // Same pattern as the palette: note the opener before taking the focus (StrictMode-safe), so the
    // keys typed right after `N` land in the field and Esc can give the focus back.
    useLayoutEffect(() => {
        if (document.activeElement !== fieldRef.current)
            openerRef.current = document.activeElement;
        fieldRef.current?.focus();
    }, []);
    const close = () => {
        onClose();
        openerRef.current?.focus?.();
    };
    // Esc closes the capture wherever the focus is: Safari leaves it on the page body after a click
    // on the switch or the select, outside the dialog's own key handler.
    const closeRef = useRef(close);
    useEffect(() => {
        closeRef.current = close;
    });
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented)
                return;
            event.preventDefault();
            closeRef.current();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);
    const save = () => {
        if (text.trim() === '') {
            fieldRef.current?.focus();
            return;
        }
        const keep = keepOpen;
        void Promise.resolve(onSave()).then((saved) => {
            if (saved && !keep)
                openerRef.current?.focus?.();
            else
                fieldRef.current?.focus();
        });
    };
    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        // Keep the focus inside the dialog (aria-modal).
        if (event.key === 'Tab' && panelRef.current) {
            const focusable = [
                ...panelRef.current.querySelectorAll('button:not([disabled]), textarea, select, input'),
            ];
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        }
    };
    const onFieldKeyDown = (event) => {
        // A capture is one line of thought: Enter saves; Shift+Enter still breaks the line.
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            save();
        }
    };
    return (_jsxs("div", { className: "capture", role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, onKeyDown: onKeyDown, children: [_jsx("button", { type: "button", className: "capture__scrim", tabIndex: -1, "aria-hidden": "true", onClick: close }), _jsxs("div", { className: "capture__panel", ref: panelRef, children: [_jsxs("div", { className: "capture__head", children: [_jsx("button", { type: "button", className: "capture__cancel", onClick: close, children: labels.cancel }), _jsx("h2", { id: titleId, className: "capture__title", children: labels.title }), _jsx("button", { type: "button", className: "capture__save", onClick: save, children: labels.save })] }), _jsx(HighlightedField, { ref: fieldRef, id: fieldId, className: "capture__editor", mirrorTestId: "capture-mirror", label: labels.field, describedBy: error ? errorId : undefined, invalid: Boolean(error), placeholder: labels.placeholder, value: text, marks: chips, onChange: onTextChange, onKeyDown: onFieldKeyDown }), error ? (_jsx("p", { id: errorId, className: "capture__error", role: "alert", children: error })) : null, _jsx("p", { className: "visually-hidden", "aria-live": "polite", children: chips.length > 0 ? labels.announce(chips) : '' }), chips.length > 0 ? (_jsxs("section", { className: "capture__detected", "aria-label": labels.detected, children: [_jsx("h3", { className: "capture__label", children: labels.detected }), _jsx("ul", { className: "capture__chips", children: chips.map((chip) => (_jsxs("li", { className: "capture__chip", children: [_jsx("span", { className: "capture__chip-label", children: chip.label }), _jsx("span", { className: "capture__chip-value", children: chip.value }), chip.isNew ? _jsx("span", { className: "capture__chip-new", children: labels.newTag }) : null, _jsx("button", { type: "button", className: "capture__chip-remove", "aria-label": labels.remove(chip), onClick: () => {
                                                onDismissChip(chip.key);
                                                fieldRef.current?.focus();
                                            }, children: "\u00D7" })] }, chip.key))) })] })) : null, _jsxs("div", { className: "capture__options", children: [_jsxs("div", { className: "capture__option", children: [_jsx("label", { htmlFor: destinationFieldId, children: labels.saveIn }), _jsx("select", { id: destinationFieldId, value: destinationId, onChange: (event) => onDestinationChange(event.target.value), children: destinations.map((destination) => (_jsx("option", { value: destination.id, children: destination.name }, destination.id))) })] }), _jsxs("label", { className: "capture__option", children: [_jsx("span", { children: labels.keepOpen }), _jsx("input", { type: "checkbox", role: "switch", className: "capture__switch", checked: keepOpen, onChange: (event) => onKeepOpenChange(event.target.checked) })] })] }), _jsx("p", { className: "capture__hint", children: labels.hint })] })] }));
}
//# sourceMappingURL=CaptureSheet.js.map